import { clone, concat, every, flatten, filter, find, groupBy, isEmpty, keys, omit, map, sortBy } from 'lodash';
import assets from '@api/assets.js';
import eventBus from '@api/eventBus.js';
import resource from '@api/resource.js';
import { DateTime } from 'luxon';
const _ = { clone, concat, every, flatten, filter, find, groupBy, isEmpty, keys, omit, map, sortBy };

export const state = {
  cache: {},
  environment: {},
  isLoadingTableOfContents: false,
  tableOfContents: {},
  tableOfContentsErrorMessage: null,
  watDates: [],
  watDatesIsLoading: false,
  watDateErrorMessage: null
};

export const getters = {
  getCacheForKey: state => key => state.cache[key],
  environment: state => state.environment,
  findReportForProperties: state => (searchObject) => {
    let foundResult = null;

    // we're looking by report id
    if (searchObject.id) {
      // first see if it's in any first level reports array
      _.find(state.tableOfContents.reportData, (category) => {
        foundResult = _.find(category.reports, report => (report.id || '').toLowerCase() === searchObject.id.toLowerCase());

        // if not found at the category level, try look in the groups' reports
        if (!foundResult) {
          foundResult = _.find(_.flatten(_.map(category.groups, group => group.reports)), report => (report.id || '').toLowerCase() === searchObject.id.toLowerCase());
        }
        return foundResult;
      });
      return foundResult;
    }
    else {
      // handle different types of lookups
      return null;
    }
  },
  hasEnvironmentData: state => !_.isEmpty(state.environment),
  isLoadingTableOfContents: state => state.isLoadingTableOfContents,
  tableOfContents: state => state.tableOfContents,
  tableOfContentsIsEmpty: state => _.isEmpty(state.tableOfContents.reportData),
  tableOfContentsErrorMessage: state => state.tableOfContentsErrorMessage,
  watDates: state => state.watDates,
  activeWatDates: (state) => {
    const now = DateTime.now();
    if (_.every(state.watDates, date => date.isActive && now > DateTime.fromISO(date.date))) {
      return [];
    }

    // there's at least date that hasn't happened yet
    return _.filter(state.watDates, date => date.isActive);
  },
  watDatesIsLoading: state => state.watDatesIsLoading,
  watDateErrorMessage: state => state.watDateErrorMessage
};

export const mutations = {
  setDataCache: (state, data) => {
    state.cache[data.key] = data.data;
  },
  setEnvironment: (state, environment) => {
    state.environment = environment;
  },
  setIsLoadingTableOfContents: (state, isLoading) => {
    state.isLoadingTableOfContents = isLoading;
  },
  setTableOfContents: (state, tableOfContents) => {
    state.tableOfContents = tableOfContents;
  },
  setTableOfContentsErrorMessage: (state, errorMessage) => {
    state.tableOfContentsErrorMessage = errorMessage;
  },
  setWatDates: (state, dates) => {
    state.watDates = dates;
  },
  setWatDatesIsLoading: (state, isLoading) => {
    state.watDatesIsLoading = isLoading;
  },
  setWatDateErrorMessage: (state, errorMessage) => {
    state.watDateErrorMessage = errorMessage;
  }
};

/* Actions should always return a promise */
export const actions = {
  setDataCache: async ({ commit }, data) => {
    commit('setDataCache', data);
  },
  setEnvironment: async ({ commit }, environment) => {
    commit('setEnvironment', environment);
  },
  loadAndGetEnvironment: async ({ commit, dispatch }, override) => {
    if (override || _.isEmpty(state.environment)) {
      const envData = await resource.get(`${resource.BASE_API_PATH}/w/environment`);
      commit('setEnvironment', envData);

      // fire the event AFTER it's saved
      eventBus.bus.$emit(eventBus.CHANNEL_ENVIRONMENT_LOADED, true);
      dispatch('loadTableOfContents', true);
    }

    return state.environment;
  },
  loadTableOfContents: async ({ commit }, override) => {
    if (override || (state.tableOfContents.reportData.length === 0 && !state.isLoadingTableOfContents)) {
      commit('setIsLoadingTableOfContents', true);

      // fetch it
      try {
        const result = await assets.get('json/reportReleaseSchedule.json');
        // sort the results
        result.reportData = _.map(result.reportData, (category) => {
          category.groups = _.map(category.groups, (group) => {
            group.reports = _.sortBy(group.reports, 'sort');
            return group;
          });
          category.groups = _.sortBy(category.groups, 'sort');
          category.reports = _.sortBy(category.reports, 'sort');
          return category;
        });

        //sort the categories
        result.reportData = _.sortBy(result.reportData, 'sort');
        commit('setTableOfContents', result);

      }
      catch (err) {
        console.log(err);
        commit('setTableOfContentsErrorMessage', 'There was a problem loading the table of contents, please try again later.');
      }
      finally {
        commit('setIsLoadingTableOfContents', false);

        // fire the event
        eventBus.bus.$emit(eventBus.CHANNEL_TOC_LOADED, true);
      }
    }
  },
  loadWatDates: async ({ commit }) => {
    commit('setWatDatesIsLoading', true);
    try {
      const watDates = await resource.get(`${resource.BASE_API_PATH}/w/facstaff/wat/announcements/dates`);
      commit('setWatDates', watDates.items);
    }
    catch (err) {
      commit('setWatDateErrorMessage', 'There was an error retrieving the WAT Announcements');
    }
    finally {
      commit('setWatDatesIsLoading', false);
    }
  }
};

export default { state, getters, mutations, actions, namespaced: true };